import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosTokenApi } from "../../../utils/axios";
import { Button, Input, Select, Radio, Checkbox } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import moment from "moment";

const { TextArea } = Input;

const QuestionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [items, setItems] = useState([]);
  const [videos, setVideos] = useState([]);
  const [sounds, setSounds] = useState([]);

  // Form state
  const [answerType, setAnswerType] = useState("text"); // text, text_video, text_sound
  const [content, setContent] = useState("");
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Fetch question details
    axiosTokenApi
      .get(process.env.REACT_APP_API + "/questions/" + id)
      .then((res) => {
        setQuestion(res.data);
        setIsVisible(res.data.is_visible !== false); // デフォルトtrue
        if (res.data.answer) {
          setAnswer(res.data.answer);
          setContent(res.data.answer.content || "");
          setSelectedItemId(res.data.answer.item_id);

          // Determine answer type based on item_id
          if (res.data.answer.item_id) {
            const item = res.data.answer.item;
            if (item && item.type === '動画') {
              setAnswerType("text_video");
            } else if (item && item.type === '音声') {
              setAnswerType("text_sound");
            } else {
              setAnswerType("text");
            }
          } else {
            setAnswerType("text");
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching question:", err);
        alert("質問の取得に失敗しました。");
      });

    // Fetch items
    axiosTokenApi
      .get(process.env.REACT_APP_API + "/items")
      .then((res) => {
        setItems(res.data);
        // Filter videos and sounds for easier selection
        const videoItems = res.data.filter(item => item.type === '動画');
        const soundItems = res.data.filter(item => item.type === '音声');
        setVideos(videoItems);
        setSounds(soundItems);
      })
      .catch((err) => {
        console.error("Error fetching items:", err);
      });
  }, [id]);

  const handleSubmit = async () => {
    // Validate - テキスト回答は常に必須
    if (!content || content.trim() === "") {
      alert("テキスト回答を入力してください。");
      return;
    }
    if (
      (answerType === "text_video" || answerType === "text_sound") &&
      !selectedItemId
    ) {
      alert("コンテンツを選択してください。");
      return;
    }

    const answerData = {
      question_id: parseInt(id),
      content: content, // テキスト回答は常に必須
      item_id:
        answerType === "text_video" || answerType === "text_sound"
          ? selectedItemId
          : null,
    };

    try {
      if (answer) {
        // Update existing answer
        await axiosTokenApi.put(
          process.env.REACT_APP_API + "/answers/" + answer.id,
          answerData
        );
      } else {
        // Create new answer
        await axiosTokenApi.post(
          process.env.REACT_APP_API + "/answers",
          answerData
        );
      }

      // Update question visibility if changed
      if (question.is_visible !== isVisible) {
        await axiosTokenApi.patch(
          process.env.REACT_APP_API + "/questions/" + id + "/toggle-visibility"
        );
      }

      alert(answer ? "回答を更新しました。" : "回答を作成しました。");
      navigate("/manage/question");
    } catch (err) {
      console.error("Error saving:", err);
      alert(answer ? "回答の更新に失敗しました。" : "回答の作成に失敗しました。");
    }
  };

  const handleBack = () => {
    navigate("/manage/question");
  };

  if (!question) {
    return <div className="p-10">読み込み中...</div>;
  }

  return (
    <div className="p-10 bg-white shadow-md mx-auto my-3 overflow-y-auto main">
      <div className="w-full h-10 mb-2">
        <p className="text-2xl text-left mx-10 font-bold">
          回答作成/編集
        </p>
      </div>

      <div className="w-full mb-2 flex">
        <div className="w-full h-10 px-10 flex">
          <div className="w-1/4 text-center text-sm font-medium py-2">
            質問ID
          </div>
          <Input
            value={question.id}
            type="text"
            className="w-3/4"
            disabled
          />
        </div>
      </div>

      <div className="w-full mb-2 flex">
        <div className="w-full h-10 px-10 flex">
          <div className="w-1/4 text-center text-sm font-medium py-2">
            投稿日時
          </div>
          <Input
            value={moment(question.created_at)
              .tz("Asia/Tokyo")
              .format("YYYY-MM-DD HH:mm")}
            type="text"
            className="w-3/4"
            disabled
          />
        </div>
      </div>

      <div className="w-full mb-2 flex">
        <div className="w-full h-10 px-10 flex">
          <div className="w-1/4 text-center text-sm font-medium py-2">
            ニックネーム
          </div>
          <Input
            value={question.nickname}
            type="text"
            className="w-3/4"
            disabled
          />
        </div>
      </div>

      <div className="w-full mb-2 flex">
        <div className="w-full h-10 px-10 flex">
          <div className="w-1/4 text-center text-sm font-medium py-2">
            ユーザーID
          </div>
          <Input
            value={question.user_id}
            type="text"
            className="w-3/4"
            disabled
          />
        </div>
      </div>

      <div className="w-full mb-2 flex">
        <div className="w-full h-10 px-10 flex">
          <div className="w-1/4 text-center text-sm font-medium py-2">
            言語
          </div>
          <Input
            value={(question.language || 'ja') === 'ja' ? '日本語' : '英語'}
            type="text"
            className="w-3/4"
            disabled
          />
        </div>
      </div>

      <div className="w-full mb-2 flex">
        <div className="w-full h-20 px-10 flex">
          <div className="w-1/4 text-center text-sm font-medium py-2">
            質問内容
          </div>
          <TextArea
            value={question.content}
            rows={6}
            className="w-3/4 h-20"
            disabled
          />
        </div>
      </div>

      <div className="w-full mb-4 flex">
        <div className="w-full h-10 px-10 flex items-center">
          <div className="w-1/4 text-center text-sm font-medium py-2">
            公開設定
          </div>
          <div className="w-3/4 flex items-center">
            <Checkbox
              checked={isVisible}
              onChange={(e) => setIsVisible(e.target.checked)}
            >
              公開
            </Checkbox>
          </div>
        </div>
      </div>

      <div className="w-full mb-2 flex">
        <div className="w-full h-10 px-10 flex">
          <div className="w-1/4 text-center text-sm font-medium py-2">
            回答形式
          </div>
          <div className="w-3/4 text-left py-2">
            <Radio.Group
              value={answerType}
              onChange={(e) => {
                const newType = e.target.value;
                setAnswerType(newType);
                // 回答形式が変わったらselectedItemIdをクリア
                if (newType === "text") {
                  setSelectedItemId(null);
                } else if (newType === "text_video" && answerType === "text_sound") {
                  // 音声から動画に変更した場合はクリア
                  setSelectedItemId(null);
                } else if (newType === "text_sound" && answerType === "text_video") {
                  // 動画から音声に変更した場合はクリア
                  setSelectedItemId(null);
                }
              }}
            >
              <Radio value="text">テキストのみ</Radio>
              <Radio value="text_video">テキスト + 動画</Radio>
              <Radio value="text_sound">テキスト + 音声</Radio>
            </Radio.Group>
          </div>
        </div>
      </div>

      {/* Text Content - 常に表示（必須） */}
      <div className="w-full mb-2 flex">
        <div className="w-full h-32 px-10 flex">
          <div className="w-1/4 text-center text-sm font-medium py-2">
            テキスト回答 <span className="text-red-500">*</span>
          </div>
          <TextArea
            rows={12}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="回答内容を入力してください（必須）"
            className="w-3/4 h-32"
          />
        </div>
      </div>

      {/* Video Selection */}
      {answerType === "text_video" && (
        <div className="w-full mb-4 flex">
          <div className="w-full px-10 flex">
            <div className="w-1/4 text-center text-sm font-medium py-2">
              回答動画
            </div>
            <Select
              className="w-3/4"
              placeholder="動画を選択してください"
              value={selectedItemId || undefined}
              onChange={(value) => setSelectedItemId(value)}
              showSearch
              optionFilterProp="label"
              options={videos.map((video) => ({
                value: video.id,
                label: `${video.id} - ${video.japanesetitle}`,
              }))}
            />
          </div>
        </div>
      )}

      {/* Sound Selection */}
      {answerType === "text_sound" && (
        <div className="w-full mb-4 flex">
          <div className="w-full px-10 flex">
            <div className="w-1/4 text-center text-sm font-medium py-2">
              回答音声
            </div>
            <Select
              className="w-3/4"
              placeholder="音声を選択してください"
              value={selectedItemId || undefined}
              onChange={(value) => setSelectedItemId(value)}
              showSearch
              optionFilterProp="label"
              options={sounds.map((sound) => ({
                value: sound.id,
                label: `${sound.id} - ${sound.japanesetitle}`,
              }))}
            />
          </div>
        </div>
      )}

      <div className="w-full text-center mt-24 flex">
        <div className="w-1/4 text-center">
          <Button onClick={handleBack} className="w-48 m-auto">
            <ArrowLeftOutlined /> 戻る
          </Button>
        </div>
        <div className="w-3/4 text-center">
          <Button
            type="primary"
            onClick={handleSubmit}
            className="w-80 text-black border-gray-300"
          >
            <SaveOutlined /> {answer ? "更新" : "保存"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuestionDetail;
